import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { SortAscending, SortDescending } from "@phosphor-icons/react"
import Filter from "../../../components/TableFilters"
import Loader from "../../../components/Loader"
import cn from "../../../utils/cn"

export const StatisticsTable = ({
  loading = false,
  data = [],
  sorting,
  onSortingChange,
  totalArr = [],
  columns,
  showTotalBar = true,
}) => {
  const table = useReactTable({
    columns,
    data: loading ? [] : data || [],
    manualPagination: true,
    enableManualSorting: true,
    manualFiltering: true,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    pageCount: loading ? 0 : data?.pages,
    onSortingChange,
    enableSortingRemoval: false,
    state: { sorting },
  })
  return (
    <div className="overflow-x-auto max-h-[449px] bg-white rounded-b-lg">
      <table className="min-w-full bg-white border-collapse">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr
              key={headerGroup.id}
              className="overflow-hidden relative overflow-x-auto"
            >
              {headerGroup.headers.map((column) => (
                <th
                  key={column.id}
                  className={cn(
                    "px-4 py-2 border-b border-r border-gray-200 bg-gray-100 align-top sticky top-0 text-sm",
                    column.id === "PatientIssuer" ? "text-left" : "text-center"
                  )}
                >
                  <div
                    className={cn(
                      "flex flex-col",
                      column.id === "PatientIssuer"
                        ? "items-start"
                        : "justify-center items-center"
                    )}
                  >
                    <button
                      {...{
                        className: column.column.getCanSort()
                          ? cn(
                              "w-full select-none cursor-pointer flex items-center gap-1",
                              column.id === "PatientIssuer"
                                ? "justify-start"
                                : "justify-center"
                            )
                          : "w-full",
                        onClick: column.column.getToggleSortingHandler(),
                      }}
                    >
                      {flexRender((context) => {
                        const header = column.column.columnDef.header
                        if (typeof header === "string") {
                          return header.toUpperCase()
                        }
                        if (typeof header === "function") {
                          const result = header(context)
                          return typeof result === "string"
                            ? result.toUpperCase()
                            : result
                        }
                        return header
                      }, column.getContext())}
                      {{
                        asc: <SortAscending className="h-4 w-4" />,
                        desc: <SortDescending className="h-4 w-4" />,
                      }[column.column.getIsSorted()] ?? null}
                    </button>
                    {column.column.getCanFilter() ? (
                      <div className="mt-2">
                        <Filter column={column.column} />
                      </div>
                    ) : null}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        {loading ? (
          <tbody>
            <tr>
              <td colSpan={table.getAllColumns().length}>
                <div className="py-10 flex justify-center items-center gap-3">
                  <Loader className="border-primary" />
                  <p>Loading Results...</p>
                </div>
              </td>
            </tr>
          </tbody>
        ) : (
          <>
            {data?.length > 0 ? (
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-100">
                    {row.getVisibleCells().map((cell, cellIndex) => (
                      <td
                        key={cell.id}
                        className={cn(
                          "px-4 py-2 border-b border-r border-gray-200",
                          cell.column.id === "PatientIssuer" || cellIndex === 0
                            ? "text-left"
                            : "text-center"
                        )}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
                {data?.length > 0 && showTotalBar && totalArr.length > 0 && (
                  <tr>
                    <th className="px-4 py-2 border-r border-gray-200 bg-gray-100 text-left rounded-bl-lg">
                      Total
                    </th>
                    {totalArr.map((item, index) => {
                      if (item === null || item === undefined) {
                        return (
                          <th
                            key={index}
                            className={cn(
                              "px-4 py-2 border-r border-gray-200 bg-gray-100 text-center",
                              index === totalArr.length - 1
                                ? "rounded-br-lg"
                                : ""
                            )}
                          ></th>
                        )
                      }
                      return (
                        <th
                          key={index}
                          className={cn(
                            "px-4 py-2 border-r border-gray-200 bg-gray-100 text-center",
                            index === totalArr.length - 1 ? "rounded-br-lg" : ""
                          )}
                        >
                          {item}
                        </th>
                      )
                    })}
                  </tr>
                )}
              </tbody>
            ) : (
              <tr>
                <td
                  className="rounded-b-lg"
                  colSpan={table.getAllColumns().length}
                >
                  <div className="mt-3 text-center py-10">No results found</div>
                </td>
              </tr>
            )}
          </>
        )}
      </table>
    </div>
  )
}

export default StatisticsTable
